import { getAuthToken } from 'utils/auth';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

async function parseResponse(response, path) {
  const contentType = response.headers.get('content-type') || '';
  const rawText = await response.text();
  let payload = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = null;
    }
  }

  if (response.ok && !contentType.includes('application/json')) {
    const preview = rawText ? rawText.slice(0, 240) : 'empty body';
    throw new Error(`Unexpected response format on ${path}. Expected JSON but got "${contentType || 'unknown'}". Body: ${preview}`);
  }

  if (response.ok && payload === null) {
    throw new Error(`Empty or invalid JSON response on ${path}.`);
  }

  if (!response.ok) {
    const validation = payload?.errors && typeof payload.errors === 'object' ? Object.values(payload.errors).flat()?.[0] : null;
    throw new Error(validation || payload?.message || `Request failed: ${response.status} on ${path}`);
  }

  return payload;
}

async function request(path, options = {}) {
  const token = getAuthToken();
  const { method = 'GET', body } = options;
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : ''
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
  } catch {
    throw new Error(`Network error calling ${API_BASE_URL}${path}. Check Vite proxy and backend availability.`);
  }

  return parseResponse(response, path);
}

async function requestFormData(path, formData) {
  const token = getAuthToken();
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: token ? `Bearer ${token}` : ''
      },
      body: formData
    });
  } catch {
    throw new Error(`Network error calling ${API_BASE_URL}${path}. Check Vite proxy and backend availability.`);
  }

  return parseResponse(response, path);
}

export const chamaApi = {
  groups: () => request('/groups'),
  createGroup: (payload) => request('/groups', { method: 'POST', body: payload }),
  createMember: (groupId, payload) => request(`/groups/${groupId}/members`, { method: 'POST', body: payload }),
  createLoan: (groupId, payload) => request(`/groups/${groupId}/loans`, { method: 'POST', body: payload }),
  createMeeting: (groupId, payload) => request(`/groups/${groupId}/meetings`, { method: 'POST', body: payload }),
  createContribution: (groupId, payload) => request(`/groups/${groupId}/contributions`, { method: 'POST', body: payload }),
  retryContributionStatus: (groupId, contributionId) =>
    request(`/groups/${groupId}/contributions/${contributionId}/retry-status`, { method: 'POST' }),
  groupDetail: (groupId) => request(`/groups/${groupId}`),
  users: () => request('/users'),
  myRoles: () => request('/users/me/roles'),
  updateUser: (userId, payload) => request(`/users/${userId}`, { method: 'PUT', body: payload }),
  smsMessages: () => request('/sms-messages'),
  groupContributions: (groupId) => request(`/groups/${groupId}/contributions`),
  groupContributionSummary: (groupId) => request(`/groups/${groupId}/contributions/summary`),
  groupLoans: (groupId) => request(`/groups/${groupId}/loans`),
  approveLoan: (groupId, loanId) => request(`/groups/${groupId}/loans/${loanId}/approve`, { method: 'PUT' }),
  rejectLoan: (groupId, loanId) => request(`/groups/${groupId}/loans/${loanId}/reject`, { method: 'PUT' }),
  walletTransactions: () => request('/wallet/transactions'),
  walletCallbackEvents: ({ limit = 100, channel = '', unmatchedOnly = false } = {}) => {
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    if (channel) query.set('channel', channel);
    if (unmatchedOnly) query.set('unmatched_only', '1');
    return request(`/wallet/callback-events?${query.toString()}`);
  },
  mobileInactivitySetting: () => request('/settings/mobile-inactivity'),
  updateMobileInactivitySetting: (value) => request('/settings/mobile-inactivity', { method: 'PUT', body: { value } }),
  walletMe: () => request('/wallet/me'),
  dataPackages: () => request('/wallet/data-packages'),
  buyAirtime: (payload) => request('/wallet/buy-airtime', { method: 'POST', body: payload }),
  buyData: (payload) => request('/wallet/buy-data', { method: 'POST', body: payload }),
  processPendingPurchase: (transactionId, payload = {}) =>
    request(`/wallet/transactions/${transactionId}/process-pending-purchase`, { method: 'POST', body: payload }),
  fetchAirtimeStatus: (transactionId) =>
    request(`/wallet/transactions/${transactionId}/airtime-status`, { method: 'POST' }),
  airtimeTrail: (transactionId) =>
    request(`/wallet/transactions/${transactionId}/airtime-trail`),
  reconcileWalletTransaction: (transactionId) => request(`/wallet/transactions/${transactionId}/reconcile`, { method: 'POST' }),
  groupMeetings: (groupId) => request(`/groups/${groupId}/meetings`),
  groupMeetingDetail: (groupId, meetingId) => request(`/groups/${groupId}/meetings/${meetingId}`),
  saveMeetingMinutes: (groupId, meetingId, minutes) => request(`/groups/${groupId}/meetings/${meetingId}/minutes`, { method: 'POST', body: { minutes } }),
  organizeMeetingNotes: (groupId, meetingId, notes) => request(`/groups/${groupId}/meetings/${meetingId}/organize-notes`, { method: 'POST', body: { notes } }),
  uploadMeetingAttachment: (groupId, meetingId, attachmentType, file) => {
    const formData = new FormData();
    formData.append('attachment_type', attachmentType);
    formData.append('file', file);
    return requestFormData(`/groups/${groupId}/meetings/${meetingId}/attachments`, formData);
  },
  notifications: () => request('/notifications'),
  contactMessages: () => request('/contact-messages'),
  deleteContactMessage: (id) => request(`/contact-messages/${id}`, { method: 'DELETE' }),
  adminApplications: () => request('/admin-applications'),
  approveAdminApplication: (id) => request(`/admin-applications/${id}/approve`, { method: 'PUT' }),
  rejectAdminApplication: (id) => request(`/admin-applications/${id}/reject`, { method: 'PUT' }),
  plans: () => request('/plans'),
  adminPlans: () => request('/admin/plans'),
  createPlan: (payload) => request('/plans', { method: 'POST', body: payload }),
  updatePlan: (id, payload) => request(`/plans/${id}`, { method: 'PUT', body: payload }),
  deletePlan: (id) => request(`/plans/${id}`, { method: 'DELETE' }),
  groupSubscription: (groupId) => request(`/groups/${groupId}/subscription`),
  subscribeGroup: (groupId, payload) => request(`/groups/${groupId}/subscribe`, { method: 'POST', body: payload }),
  allSubscriptions: () => request('/subscriptions'),
  groupReportSummary: (groupId) => request(`/groups/${groupId}/reports/summary`),
  aiChat: (message, history = []) => request('/ai/chat', { method: 'POST', body: { message, history } })
};
